import { Card, CardContent } from "@/components/ui/card";
import { Button } from "./ui/button";
import { ArrowRight, UtensilsCrossed, Hotel, HeartPulse, Warehouse, GraduationCap, ShoppingBag, Sparkles, Church } from "lucide-react";
import { Link } from "react-router-dom";

const CaseStudyCards = () => {
  const caseStudies = [
    {
      Icon: UtensilsCrossed,
      industry: "Food & Beverage Operations",
      challenge: "Staff burnout from dish return, bussing, and slow table service during peak hours.",
      solution: "BellaBot Pro, HolaBot, KettyBot Pro + AI voice agents for reservations and follow-up.",
      impact: "Reduced repetitive labor, faster dish return, and improved guest experience.",
    },
    {
      Icon: Hotel,
      industry: "Hospitality & Guest Services",
      challenge: "Overstretched front-desk and housekeeping teams handling amenity and delivery requests.",
      solution: "FlashBot Max & KettyBot Pro for multi-floor delivery; CC1 Pro for lobby and floor care.",
      impact: "Improved guest response times and supported staff by reducing manual delivery runs.",
    },
    {
      Icon: HeartPulse,
      industry: "Health Care Support",
      challenge: "Repetitive transport tasks and sanitation consistency across long shifts.",
      solution: "CC1 Pro, FlashBot Max, BG1 & MT1 Series with AI workflow routing.",
      impact: "Helped reduce staff strain and supported consistent sanitation coverage.",
    },
    {
      Icon: Warehouse,
      industry: "Warehouse & Logistics",
      challenge: "Heavy material movement and large-area cleaning under labor shortages.",
      solution: "T300, T600, MT1 Max & D5 with task routing and reporting automation.",
      impact: "Supported continuous operations and reduced manual transport workload.",
    },
    {
      Icon: GraduationCap,
      industry: "Education & Campuses",
      challenge: "Maintaining clean, safe facilities across large campus footprints.",
      solution: "CC1 Pro & MT1 Series for autonomous floor care between class periods.",
      impact: "Improved cleaning coverage and freed staff for higher-value work.",
    },
    {
      Icon: ShoppingBag,
      industry: "Retail & Property Services",
      challenge: "Inconsistent floor care and limited guest engagement on busy days.",
      solution: "KettyBot Pro for guided marketing; CC1 Pro and SH1 for floor care.",
      impact: "Supported consistent presentation and improved customer communication.",
    },
    {
      Icon: Sparkles,
      industry: "Commercial Cleaning",
      challenge: "Scaling cleaning programs across multiple properties with tight labor.",
      solution: "CC1 Pro, MT1 Max, BG1 & SH1 deployed across recurring service routes.",
      impact: "Helped improve workflow efficiency and operational consistency.",
    },
    {
      Icon: Church,
      industry: "Churches & Event Venues",
      challenge: "Fast turnaround cleaning between services and large events.",
      solution: "CC1 Pro & BG1 for autonomous floor care during off-hours.",
      impact: "Reduced manual strain on volunteer and event teams.",
    },
  ];

  return (
    <section className="py-20">
      <div className="text-center mb-12">
        <h2 className="font-orbitron font-bold text-4xl md:text-5xl mb-4">
          Real Operations. <span className="text-primary">Real-World Automation Impact.</span>
        </h2>
        <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
          How AI automation and PUDU robotics support teams across the industries Neil AI serves.
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
        {caseStudies.map((study, index) => {
          const Icon = study.Icon;
          return (
            <Card
              key={index}
              className="glass-effect border-[hsl(var(--line))] hover:border-primary/40 transition-all duration-300 group overflow-hidden"
            >
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-[10px] font-bold text-primary uppercase tracking-wider">
                    Case Study
                  </span>
                  <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-primary" strokeWidth={1.5} />
                  </div>
                </div>

                <h3 className="font-orbitron font-bold text-base mb-3 text-foreground leading-tight">
                  {study.industry}
                </h3>

                <div className="space-y-3 text-xs">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-secondary font-semibold mb-1">Challenge</div>
                    <p className="text-muted-foreground">{study.challenge}</p>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-secondary font-semibold mb-1">Solution Stack</div>
                    <p className="text-muted-foreground">{study.solution}</p>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-secondary font-semibold mb-1">Business Impact</div>
                    <p className="text-muted-foreground">{study.impact}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="text-center">
        <Button
          asChild
          variant="outline"
          size="lg"
          className="border-2 border-primary/30 hover:bg-primary/10 hover:border-primary/60 font-bold rounded-xl px-8 py-6 group"
        >
          <Link to="/case-studies">
            View Similar Solutions
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </Button>
      </div>
    </section>
  );
};

export default CaseStudyCards;
